import { useEffect, useState } from "react";
import { Principal } from "@dfinity/principal";
import { useAdmin } from "../contexts/AdminContext";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { CopyPrincipal } from "../components/CopyPrincipal";
import { ErrorBanner } from "../components/ErrorBanner";
import { indexErrorMessage } from "../lib/utils";
import type { Admin } from "../lib/types";

export function AdminsPage() {
  const { actor, isInstaller } = useAdmin();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [removeTarget, setRemoveTarget] = useState<Admin | null>(null);
  const [removing, setRemoving] = useState(false);
  const [newPrincipal, setNewPrincipal] = useState("");
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  async function load() {
    if (!actor) return;
    try {
      const res: { ok?: Admin[]; err?: unknown } = await actor.listAdmins();
      if ("ok" in res) setAdmins(res.ok!);
      else setError("Failed to load admins");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [actor]); // eslint-disable-line react-hooks/exhaustive-deps

  async function addAdmin() {
    if (!actor || !newPrincipal.trim() || !newName.trim()) return;
    setAdding(true);
    setAddError("");
    try {
      const principal = Principal.fromText(newPrincipal.trim());
      const res: { ok?: Admin; err?: unknown } = await actor.addAdmin(principal, newName.trim());
      if ("err" in res) {
        setAddError(indexErrorMessage(res.err));
      } else {
        setNewPrincipal("");
        setNewName("");
        await load();
      }
    } catch (e) {
      setAddError(e instanceof Error ? e.message : "Invalid principal or error");
    } finally {
      setAdding(false);
    }
  }

  async function removeAdmin() {
    if (!actor || !removeTarget) return;
    setRemoving(true);
    try {
      await actor.removeAdmin(removeTarget.id);
      setRemoveTarget(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to remove");
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div>
      <h1 style={{ fontSize: 24, marginBottom: 24 }}>Admins</h1>

      {error && <ErrorBanner message={error} onDismiss={() => setError("")} />}

      {isInstaller && (
        <div className="card" style={{ marginBottom: 24, maxWidth: 520 }}>
          <h3 style={{ marginBottom: 16, fontSize: 15 }}>Add Admin</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input
              placeholder="Principal ID"
              value={newPrincipal}
              onChange={(e) => setNewPrincipal(e.target.value)}
              style={{ fontFamily: "'DM Mono', monospace", fontSize: 13 }}
            />
            <input
              placeholder="Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            {addError && <p style={{ color: "var(--color-red)", fontSize: 13 }}>{addError}</p>}
            <button
              className="btn-primary"
              onClick={addAdmin}
              disabled={adding || !newPrincipal.trim() || !newName.trim()}
              style={{ alignSelf: "flex-start" }}
            >
              {adding ? "Adding…" : "Add Admin"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: "var(--color-muted)" }}>Loading…</p>
      ) : admins.length === 0 ? (
        <p style={{ color: "var(--color-muted)" }}>No admins added yet.</p>
      ) : (
        <div className="card" style={{ padding: 0, maxWidth: 640 }}>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Principal</th>
                {isInstaller && <th></th>}
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.id.toText()}>
                  <td style={{ fontWeight: 500 }}>{admin.name}</td>
                  <td><CopyPrincipal principal={admin.id.toText()} /></td>
                  {isInstaller && (
                    <td>
                      <button
                        className="btn-danger"
                        style={{ fontSize: 12, height: 30, minHeight: 30, padding: "0 12px" }}
                        onClick={() => setRemoveTarget(admin)}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={removeTarget !== null}
        title="Remove Admin"
        message={removeTarget ? `Remove "${removeTarget.name}" from admins? They will lose dashboard access.` : ""}
        confirmLabel={removing ? "Removing…" : "Remove"}
        onConfirm={removeAdmin}
        onCancel={() => setRemoveTarget(null)}
        danger
      />
    </div>
  );
}
